/**
 * Componente Router que representa el enrutador de la aplicación.
 *
 * Maneja el ciclo de vida de la autenticación:
 *  - Mientras Firebase resuelve la sesión inicial, muestra <Loading/>
 *  - Cuando hay usuario, intenta cargar sus roles
 *  - Si los roles fallan por error TRANSITORIO (red, offline, deadline),
 *    mantiene al usuario autenticado y reintenta con backoff exponencial
 *  - Solo desloguea cuando Firebase reporta `user === null` (sesión real
 *    perdida) o cuando el error es PERMANENTE (permissions, not-found...)
 */
import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { BrowserRouter, Route } from "react-router-dom";

import { AUTH } from '../../application/types';
import { roles } from '../actions';
import { Home } from '../components/home';
import { Login } from '../components/login';
import { Loading } from '../components/loading';
import { ResetPassword } from '../components/login/reset_password';
import { MfaVerifyEmail } from '../components/mfa/verify_email';
import { MfaEnroll } from '../components/mfa/enroll';
import { logClient } from '../lib/client_log';

// Errores que se consideran transitorios: provocados por la red o el cliente,
// no por la configuración del usuario. NO deben tumbar la sesión.
const isTransientError = (e) => {
  if (!e) return false;
  const code = e.code || '';
  const msg  = (e.message || '').toString().toLowerCase();
  return (
    code === 'unavailable' ||
    code === 'cancelled' ||
    code === 'deadline-exceeded' ||
    code === 'internal' ||
    code === 'aborted' ||
    code === 'resource-exhausted' ||
    code === 'unknown' ||
    msg.includes('offline') ||
    msg.includes('network') ||
    msg.includes('failed to fetch') ||
    msg.includes('client is offline')
  );
};

// Vida máxima de una sesión desde el inicio de sesión (auth_time):
//  - usuarios normales: 24 horas
//  - root (claim rootUser, sincronizado por syncRootUserClaim): 7 días
// Cumplido el plazo se fuerza signOut → el usuario vuelve a Login (y con 2FA
// activo, re-verifica su código). Las reglas de Firestore aplican el mismo
// límite server-side (sessionFresh), así que esto no es solo cosmético.
const SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const ROOT_SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const isSessionExpired = (claims) => {
  const cap = claims.rootUser === true ? ROOT_SESSION_MAX_AGE_MS : SESSION_MAX_AGE_MS;
  const authTimeMs = Number(claims.auth_time || 0) * 1000;
  return authTimeMs > 0 && (Date.now() - authTimeMs) > cap;
};

// Gate de autenticación en 2 pasos. Se evalúa con los claims del ID token
// (lectura LOCAL, sin red ni Firestore — crítico: para usuarios con método
// email las rules bloquean Firestore hasta verificar el OTP).
//  - 'email': método email sin clearance de ESTE login (mfaAuthTime !== auth_time)
//  - 'enroll': el admin exige 2FA y no hay ningún factor registrado
//  - null: sin gate (usuario legacy, TOTP ya resuelto, o email ya verificado)
const getMfaGate = (user, claims) => {
  if (claims.mfaMethod === 'email'
      && Number(claims.mfaAuthTime || 0) !== Number(claims.auth_time)) {
    return 'email';
  }
  const hasFactor = (user.multiFactor?.enrolledFactors?.length > 0) || !!claims.mfaMethod;
  if (claims.mfaRequired === true && !hasFactor) return 'enroll';
  return null;
};

export const Router = () => {

  const dispatch = useDispatch();
  const auth = useSelector(state => state.get('authenticate').get('login'));
  const firebase = useFirebase();

  // Referencia al timer de retry para poderlo cancelar entre cambios de auth
  const retryRef = useRef({ timer: null });

  // Referencia a evaluateSession (vive dentro del useEffect) para poder
  // re-evaluar el gate desde los callbacks de las pantallas MFA y del
  // refresh periódico de token.
  const evaluateRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Snapshot del objeto del ref. useRef nunca reasigna `.current` por su
    // cuenta, solo nosotros mutamos `.timer` sobre el mismo objeto — así que
    // este snapshot ve siempre el timer actualizado y satisface a eslint.
    const retry = retryRef.current;

    // Carga roles con retry automático ante errores de red. No bloquea la
    // pantalla: el usuario ya queda LOGGEDIN aunque los roles tarden.
    const tryLoadRoles = async (user, attempt = 0) => {
      if (cancelled) return;
      try {
        await dispatch(roles({ uid: user.uid }));
        await dispatch({ type: AUTH.LOGGEDIN, user });
        if (attempt > 0) {
          logClient({
            event: 'auth.roles.recovered',
            level: 'info',
            message: `roles() OK tras ${attempt} reintentos`,
            data: { uid: user.uid, attempts: attempt },
          });
        }
      } catch (error) {
        const transient = isTransientError(error);
        console.warn(
          `[auth] roles() falló (intento ${attempt}, code=${error?.code || 'n/a'}, transient=${transient}):`,
          error?.message
        );

        logClient({
          event: 'auth.roles.failed',
          level: transient ? 'warn' : 'error',
          message: `roles() falló (transient=${transient})`,
          data: {
            uid: user.uid,
            attempt,
            code: error?.code || null,
            errMessage: error?.message || null,
            transient,
          },
        });

        if (transient) {
          // Mantener al usuario "autenticado" mientras reintentamos.
          // Algunos módulos comprobarán roles antes de operar; si están
          // vacíos, mostrarán empty state — preferible a echar al usuario.
          await dispatch({ type: AUTH.LOGGEDIN, user });
          const delay = Math.min(1000 * Math.pow(2, attempt), 30000); // 1, 2, 4 ... max 30s
          retry.timer = setTimeout(() => {
            if (!cancelled) tryLoadRoles(user, attempt + 1);
          }, delay);
        } else {
          // Error permanente: no podemos completar el login.
          await dispatch({ type: AUTH.LOGGINGIN });
        }
      }
    };

    // Evalúa el gate MFA ANTES de cargar roles: si el usuario tiene método
    // email y aún no verificó el OTP de este login, cualquier lectura de
    // Firestore sería rechazada por rules — por eso el chequeo usa solo el
    // token local. Si no hay gate, el flujo sigue idéntico al de siempre.
    const evaluateSession = async (user, attempt = 0) => {
      if (cancelled) return;
      let claims = {};
      try {
        claims = (await user.getIdTokenResult()).claims;
      } catch (e) {
        // Fallo transitorio leyendo el token: tratar como usuario sin gate;
        // tryLoadRoles tiene su propio retry y las rules siguen protegiendo.
        console.warn('[auth] getIdTokenResult falló:', e?.message);
      }
      if (isSessionExpired(claims)) {
        logClient({
          event: 'auth.session.expired',
          level: 'info',
          message: 'sesión superó las 24h — signOut forzado',
          data: { uid: user.uid, authTime: claims.auth_time || null },
        });
        // signOut dispara onAuthStateChanged(null) → LOGGINGIN → <Login/>
        firebase.auth().signOut().catch(e =>
          console.warn('[auth] signOut por expiración falló:', e?.message));
        return;
      }
      const gate = getMfaGate(user, claims);
      if (gate) {
        logClient({
          event: 'auth.mfa.gate',
          level: 'info',
          message: `gate MFA activo: ${gate}`,
          data: { uid: user.uid, gate, method: claims.mfaMethod || null },
        });
        dispatch({ type: gate === 'email' ? AUTH.MFA_REQUIRED : AUTH.MFA_ENROLL_REQUIRED });
        return;
      }
      return tryLoadRoles(user, attempt);
    };
    evaluateRef.current = evaluateSession;

    const unsub = firebase.auth().onAuthStateChanged(user => {
      // Cancela cualquier retry en curso al cambiar de usuario/estado
      if (retry.timer) {
        clearTimeout(retry.timer);
        retry.timer = null;
      }

      logClient({
        event: 'auth.state.changed',
        level: 'info',
        message: user ? 'user válido' : 'user null (sesión perdida)',
        data: {
          uid: user?.uid || null,
          email: user?.email || null,
          hasUser: !!user,
        },
      });

      if (user) {
        evaluateSession(user, 0);
      } else {
        // user === null: sesión real perdida (logout explícito, token revocado, etc.)
        dispatch({ type: AUTH.LOGGINGIN });
      }
    });

    return () => {
      cancelled = true;
      if (retry.timer) {
        clearTimeout(retry.timer);
        retry.timer = null;
      }
      if (typeof unsub === 'function') unsub();
    };
  }, [dispatch, firebase]);

  // Refresh periódico del ID token. Los custom claims (admin de empresa)
  // se setean server-side via Cloud Function (syncCustomerAdminClaims),
  // pero NO llegan al cliente hasta que el token se renueva. Firebase
  // los refresca solo cada hora; forzamos cada 30 min para que los
  // cambios de permisos sean visibles antes.
  useEffect(() => {
    if (auth?.type !== AUTH.LOGGEDIN) return;
    const interval = setInterval(() => {
      const user = firebase.auth().currentUser;
      if (user) {
        user.getIdToken(true)
          .then(async () => {
            // Si el admin activó 2FA a mitad de sesión, el claim llega con
            // este refresh: re-evaluamos SOLO el gate (sin recargar roles)
            // y, de estar activo, pasamos a la pantalla de verificación o
            // enrolamiento sin tumbar la sesión.
            const claims = (await user.getIdTokenResult()).claims;
            if (isSessionExpired(claims)) {
              logClient({
                event: 'auth.session.expired',
                level: 'info',
                message: 'sesión superó las 24h durante el refresh — signOut forzado',
                data: { uid: user.uid, authTime: claims.auth_time || null },
              });
              firebase.auth().signOut().catch(e =>
                console.warn('[auth] signOut por expiración falló:', e?.message));
              return;
            }
            const gate = getMfaGate(user, claims);
            if (gate) {
              logClient({
                event: 'auth.mfa.gate',
                level: 'warn',
                message: `gate MFA activado a mitad de sesión: ${gate}`,
                data: { uid: user.uid, gate },
              });
              dispatch({ type: gate === 'email' ? AUTH.MFA_REQUIRED : AUTH.MFA_ENROLL_REQUIRED });
            }
          })
          .catch(e => {
            // Silencioso: si falla, el SDK reintentará en su próximo ciclo
            console.warn('[auth] getIdToken refresh falló:', e?.message);
          });
      }
    }, 30 * 60 * 1000); // 30 minutos
    return () => clearInterval(interval);
  }, [auth?.type, firebase, dispatch]);

  // NOTA: el listener `beforeunload` que cerraba sesión al cerrar pestaña
  // fue removido. Firebase Auth ya persiste la sesión en localStorage por
  // defecto; forzar logout en cierre obligaba a re-loguear en cada visita.

  // Re-evalúa el gate MFA tras verificar el OTP o completar el enrolamiento.
  // El token ya fue refrescado por la acción, así que el gate se libera y
  // evaluateSession sigue con la carga de roles → LOGGEDIN.
  const reevaluate = () => {
    const user = firebase.auth().currentUser;
    if (user && evaluateRef.current) evaluateRef.current(user, 0);
  };

  return (
    <BrowserRouter>
      <div>
        <Route
          path = '/'
          render = {
            // /reset-password es pública (llega desde el correo, sin sesión):
            // se resuelve ANTES de cualquier estado de autenticación.
            () => window.location.pathname === '/reset-password'
              ? <ResetPassword />
              : auth
                ? auth?.type === AUTH.LOGGEDIN
                  ? <Home />
                  : auth?.type === AUTH.MFA_REQUIRED
                    ? <MfaVerifyEmail onVerified = { reevaluate } />
                    : auth?.type === AUTH.MFA_ENROLL_REQUIRED
                      ? <MfaEnroll onEnrolled = { reevaluate } />
                      : <Login />
                : <Loading />
          }
        />
      </div>
    </BrowserRouter>
  );

};
